import React, { Fragment, useMemo } from 'react';
import moment from 'moment';
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
	getStaffingNeedsCoverage,
	isQuarterCoveredByStaffingWork,
	shouldCountInStaffingNeeds,
} from './planStaffingNeedsUtils';

/**
 * Componente simplificado para mostrar solo empleados planificados por horas
 * 
 * Características principales:
 * - Se muestra SOLO cuando NO hay necesidades configuradas
 * - Muestra una única fila: "Empleados planificados"
 * - Cuenta TODOS los empleados planificados independientemente de su función
 * - Maneja correctamente fracciones de 15 minutos (4 celdas por columna)
 */
const PlanStaffingHourlyPlanned = (props) => {
	// Este componente solo se renderiza cuando NO hay necesidades
	const hasStaffingData = props.staffing_level_hourly_assignments && props.staffing_level_hourly_assignments.length > 0;
	
	// Si hay necesidades, no renderizar este componente
	if (hasStaffingData) {
		return null;
	}
	
	// Validar que tengamos los datos mínimos necesarios
	const hasPlanAssignments = props.plan_assignments && props.plan_assignments.length > 0;
	const hasWorkingHours = props.working_hours_list && props.working_hours_list.length > 0;
	const hasPlanDays = props.plan_days && props.plan_days.length > 0;
	
	if (!hasPlanAssignments || !hasWorkingHours || !hasPlanDays) {
		return null;
	}

	// Memoización de datos procesados
	const processedData = useMemo(() => {
		if (!props.plan_assignments || !props.working_hours_list) {
			return { quarterHourMap: new Map(), working_hours_list: [] };
		}
		
		const data = processStaffingData(props);
		return data;
	}, [props.plan_assignments, props.working_hours_list]);

	// Si no hay datos procesados, no renderizar
	if (!processedData || !processedData.quarterHourMap || processedData.quarterHourMap.size === 0) {
		return null;
	}

	return (
		<table id="tablePlannedOnly" className={`table table-bordered table-plan table-needs table-hourly-counter`}>
			<thead>
				<tr>
					<th className="main-col th-day">
						<div className="th-wrap">Planificación</div>
					</th>
					{generateStaffingHeader(props)}
				</tr>
			</thead>
			<tbody>
				{generatePlannedRow(props, processedData)}
			</tbody>
		</table>
	);
};

/**
 * Procesa los datos de planificación para optimizar el rendimiento
 * Similar a PlanStaffingHourlyImproved pero solo procesando la clave "ALL"
 */
const processStaffingData = (props) => {
	const { plan_assignments, working_hours_list } = props;
	
	// Crear mapa de planificaciones por cuarto de hora específico
	const quarterHourMap = new Map();
	
	plan_assignments.forEach(pa => {
		pa.plan_assignments_hourly_totals.forEach(dayTotal => {
			const dayKey = dayTotal.day;
			
			dayTotal.hours.forEach(hourBlock => {
				const baseHour = hourBlock.hour;
				const baseHourPadded = baseHour.toString().padStart(2, '0');
				const quarters = ['00', '15', '30', '45'];
				const hourAssignments = Array.isArray(hourBlock.hour_assignments) ? hourBlock.hour_assignments : [];

				quarters.forEach((quarter, quarterIdx) => {
					const quarterHourTime = `${baseHourPadded}:${quarter}:00Z`;
					const quarterStart = quarterIdx * 0.25;
					const quarterEnd = (quarterIdx + 1) * 0.25;

					hourAssignments.forEach(ha => {
						if (!shouldCountInStaffingNeeds(ha)) return;

						const assign = ha.assignment;
						const employee = assign.employee;
						const { startOffset, duration } = getStaffingNeedsCoverage(ha);
						if (
							!isQuarterCoveredByStaffingWork(
								quarterStart,
								quarterEnd,
								startOffset,
								duration
							)
						) {
							return;
						}

						// ALL - Contar todos los empleados
						const allKey = `${dayKey}_ALL_${quarterHourTime}`;
						if (!quarterHourMap.has(allKey)) quarterHourMap.set(allKey, { peopleSet: new Set(), people: [] });
						const allData = quarterHourMap.get(allKey);
						const empId = employee?.id;
						const empName = `${employee?.name || ''} ${employee?.surname || ''}`.trim() || 'Empleado sin nombre';
						if (empId !== undefined && !allData.peopleSet.has(empId)) {
							allData.peopleSet.add(empId);
							allData.people.push(empName);
						}
					});
				});
			});
		});
	});
	
	return { quarterHourMap, working_hours_list };
};

const getDayWorkingHoursList = (props, dayIndex) => {
	const perDay =
		props.plan_assignments?.[0]?.plans?.[dayIndex]?.working_hours_list;
	if (perDay?.length > 0) return perDay;
	return props.working_hours_list || [];
};

/**
 * Genera el header de la tabla con las columnas de días y horas
 */
const generateStaffingHeader = (props) => {
	return props.plan_days.map((day, dayIndex) => {
		const dayMoment = moment(day.day);
		const dayName = dayMoment.format("dd");
		const dayNum = dayMoment.format("DD");
		const isToday = dayMoment.isSame(moment(), 'day');
		
		// Obtener tipo de día para colores
		let dayType = null;
		if (day.details && day.details.length > 0) {
			dayType = day.details[0].special_day?.day_type || day.details[0].weekday?.day_type;
		}

		return (
			<Fragment key={dayIndex}>
				<th
					className="th-day head-hours-main transparent-header"
					style={{ 
						backgroundColor: '#f7f7f8',
						border: 'none',
						color: dayType?.color 
					}}
				>
					{/* Sin contenido para que se vea igual a las filas de abajo */}
				</th>
				{generateHourColumns(props, dayIndex)}
			</Fragment>
		);
	});
};

/**
 * Genera las columnas de horas (una celda por hora que contiene 4 cuartos de hora)
 */
const generateHourColumns = (props, dayIndex) => {
	return getDayWorkingHoursList(props, dayIndex).map((hour, hourIndex) => {
		return (
			<th key={hourIndex} className="head-hours" title={`${hour}h - Cuartos: 00, 15, 30, 45`}>
				{hour}h
			</th>
		);
	});
};

/**
 * Genera la fila de empleados planificados
 */
const generatePlannedRow = (props, processedData) => {
	const plannedData = calculatePlanned(props, processedData);
	
	return (
		<tr className="planned-row">
			<th>
				<div className="th-wrap">
					Empleados planificados
				</div>
			</th>
			{generatePlannedCells(props, plannedData)}
		</tr>
	);
};

/**
 * Calcula los empleados planificados usando la clave "ALL"
 */
const calculatePlanned = (props, processedData) => {
	const { quarterHourMap } = processedData;
	const planned = new Map();
	
	// Iterar sobre todos los días de la planificación
	props.plan_days.forEach((planDay, dayIndex) => {
		const dayKey = planDay.day;
		const dayHours = getDayWorkingHoursList(props, dayIndex);

		dayHours.forEach(hour => {
			['00', '15', '30', '45'].forEach(minute => {
				const timeString = `${hour}:${minute}`;
				const cellKey = `${dayKey}_${timeString}`;
				
				// Formatear el tiempo como lo espera el mapa
				const hh = hour.toString().padStart(2, '0');
				const quarterHourKey = `${hh}:${minute}:00Z`;
				
				// Usar la clave "ALL" para obtener TODOS los empleados planificados
				const allKey = `${dayKey}_ALL_${quarterHourKey}`;
				const allData = quarterHourMap.get(allKey);
				
				if (allData && allData.peopleSet.size > 0) {
					planned.set(cellKey, {
						actual: allData.peopleSet.size,
						assignedPeople: allData.people || []
					});
				}
			});
		});
	});
	
	return planned;
};

/**
 * Genera las celdas de planificados
 */
const generatePlannedCells = (props, plannedData) => {
	const cells = [];
	
	props.plan_days.forEach((planDay, dayIndex) => {
		cells.push(<td key={`day-${dayIndex}`} className="td-day"></td>);

		getDayWorkingHoursList(props, dayIndex).forEach((hour, hourIndex) => {
			// Generar una celda por hora que contiene 4 cuartos de hora
			const quarterCells = ['00', '15', '30', '45'].map((minute, minuteIndex) => {
				const timeString = `${hour}:${minute}`;
				const cellKey = `${planDay.day}_${timeString}`;
				const planned = plannedData.get(cellKey) || { actual: 0 };
				
				return (
					<div key={`${dayIndex}-${hourIndex}-${minuteIndex}`} className="quarter-cell">
						{renderPlannedCell(planned, timeString)}
					</div>
				);
			});
			
			cells.push(
				<td key={`${dayIndex}-${hourIndex}`} className="td-hour shift-cell planned-cell">
					<div className="quarter-hour-container">
						{quarterCells}
					</div>
				</td>
			);
		});
	});
	
	return cells;
};

/**
 * Renderiza una celda de planificados
 */
const renderPlannedCell = (planned, timeString) => {
	const { actual, assignedPeople } = planned;
	
	if (actual === 0) {
		return null;
	}
	
	const tooltipContent = (
		<Tooltip className="tooltip-staffing">
			<div>
				<strong>Planificado:</strong> {actual}<br/>
				{assignedPeople && assignedPeople.length > 0 && (
					<>
						<strong>{assignedPeople.length === 1 ? 'Empleado planificado:' : 'Empleados planificados:'}</strong><br/>
						{assignedPeople.map((person, index) => (
							<span key={index}>
								{person}
								{index < assignedPeople.length - 1 && <br/>}
							</span>
						))}
					</>
				)}
			</div>
		</Tooltip>
	);
	
	return (
		<OverlayTrigger placement="top" overlay={tooltipContent}>
			<div className="quarter-hour-cell planned">
				{actual}
			</div>
		</OverlayTrigger>
	);
};

export default PlanStaffingHourlyPlanned;